// Attendance.jsx (CRM-style polished UI)
// Requires: axios, react, react-icons
// CSS: use your polished Attendance.css

import React, { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { useNavigate } from "react-router-dom";
import { logEmployeeActivity } from "../utils/activityLogger";

// fixed imports: removed invalid FaRegFileCsv, added FiDownload
import { FaDownload, FaCalendarAlt, FaSave, FaSpinner } from "react-icons/fa";
import { FiDownload, FiRefreshCw, FiSearch, FiUser } from "react-icons/fi";
import "./Attendance.css";
import AttendanceTable from "./AttendanceTable";

/* ---------- Helper small components inside file ---------- */

function IconButton({ children, className = "", onClick, title, disabled }) {
  return (
    <button
      className={`btn ${className || ""}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  );
}

function Badge({ status }) {
  const s = (status || "").toLowerCase();
  const cls =
    s === "present" ? "badge present" :
    s === "absent" ? "badge absent" :
    s === "leave" ? "badge leave" :
    s === "half day" ? "badge half" :
    s === "late" ? "badge late" :
    "badge unknown";
  return <span className={cls}>{status || "—"}</span>;
}

/* ---------- Main Component ---------- */

export default function AttendanceCRM() {
  // Data states
  const [employees, setEmployees] = useState([]);
  const [attendanceList, setAttendanceList] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

   const navigate = useNavigate();

  // UI / saving states
  const [savingIds, setSavingIds] = useState([]);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [dirtySet, setDirtySet] = useState(new Set());
  const [search, setSearch] = useState("");

  // Settings
  const [standardHours, setStandardHours] = useState(8);
  const [standardLunchMinutes, setStandardLunchMinutes] = useState(60);

  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(null); // employee_id or null
  const [showReportModal, setShowReportModal] = useState(false);
  // LOGIN ACTIVITY


  const [reportMonth, setReportMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2, "0")}`;
  });
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Fetch functions
 const fetchEmployees = async () => {
  try {
    const res = await client.get("/users");
    setEmployees(res.data || []);
  } catch (err) {
    console.error("Fetch employees:", err);
    setEmployees([]);
  }
};

  const fetchAttendance = async (d = date) => {
    try {
      setLoading(true);
      const res = await client.get(`/attendance?date=${d}`);

      setAttendanceList(res.data || []);
    } catch (err) {
      console.error("Fetch attendance:", err);
      setAttendanceList([]);
    } finally {
      setLoading(false);
      setDirtySet(new Set());
    }
  };
 // fetch all login activity (list)


  useEffect(() => {
    fetchEmployees();
  }, []);
  
  // Map employees -> records for UI (with lunch fields)
  const recordsByEmployee = useMemo(() => {
    const map = {};
    for (const a of attendanceList || []) map[a.employee_id] = a;
    const arr = employees.map(emp => {
      const ex = map[emp.id] || {};
      return {
        employee_id: emp.id,
        employee_name: emp.name || `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || `User ${emp.id}`,
        date,
        status: ex.status || "",
        day_type: ex.day_type || null,
        in_time: ex.in_time || "",
        out_time: ex.out_time || "",
        lunch_in: ex.lunch_in || "",
        lunch_out: ex.lunch_out || "",
        remarks: ex.remarks || "",
        id: ex.id || null,
      };
    });
    // optional: sort by name
    arr.sort((a,b)=> (a.employee_name || "").localeCompare(b.employee_name || ""));
    return arr;
  }, [employees, attendanceList, date]);

  const [localRecords, setLocalRecords] = useState([]);
  useEffect(()=> setLocalRecords(recordsByEmployee), [recordsByEmployee]);

  // util: time parse/format
  const timeToMinutes = (hhmm) => {
    if (!hhmm) return null;
    const m = hhmm.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (!m) return null;
    return parseInt(m[1],10)*60 + parseInt(m[2],10);
  };
  const minutesToHHMM = (min) => {
    if (min == null || isNaN(min)) return "—";
    const sign = min < 0 ? "-" : "";
    const abs = Math.abs(min);
    const hh = Math.floor(abs/60), mm = abs%60;
    return `${sign}${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
  };
  const minsToDecimalHours = (min) => (min==null?null:Math.round((min/60)*100)/100);

  // calc worked (excludes lunch if present, else uses standard lunch minutes deduction when appropriate)
  const calcWorkedMinutes = (in_time, out_time, lunch_in, lunch_out) => {
    const inMin = timeToMinutes(in_time), outMin = timeToMinutes(out_time);
    if (inMin==null || outMin==null) return null;
    let diff = outMin - inMin; let overnight=false;
    if (diff < 0) { diff = outMin + 24*60 - inMin; overnight=true; }
    const li = timeToMinutes(lunch_in), lo = timeToMinutes(lunch_out);
    if (li != null && lo != null) {
      let lunch = lo - li;
      if (lunch < 0) lunch = lo + 24*60 - li;
      diff -= lunch;
    } else {
      if (standardLunchMinutes > 0 && diff > standardLunchMinutes) diff -= standardLunchMinutes;
    }
    return {minutes: diff, overnight};
  };

  const standardMinutes = Math.round(Number(standardHours)*60);

  // dirty management
  const markDirty = (empId) => setDirtySet(prev => { const n = new Set(prev); n.add(empId); return n; });

  // change handlers
  const handleChange = (empId, field, value) => {
    setLocalRecords(prev => prev.map(r => r.employee_id === empId ? {...r, [field]: value} : r));
    markDirty(empId);
  };
  const handleStatusChange = (empId, value) => {
    setLocalRecords(prev => prev.map(r => {
      if (r.employee_id !== empId) return r;
      const now = new Date(); const hh = String(now.getHours()).padStart(2,"0"); const mm = String(now.getMinutes()).padStart(2,"0");
      const newR = {...r, status: value};
      if (value === "Present" && !newR.in_time) newR.in_time = `${hh}:${mm}`;
      return newR;
    }));
    markDirty(empId);
  };

  // save single / all
  const saveAttendance = async (empId) => {
    const rec = localRecords.find(r=>r.employee_id === empId);
    if (!rec) return;
    // validate times
    for (const k of ["in_time","out_time","lunch_in","lunch_out"]) {
      if (rec[k] && timeToMinutes(rec[k])==null) { alert(`Invalid ${k.replace("_"," ")} for ${rec.employee_name}`); return; }
    }
    const payload = {
      employee_id: rec.employee_id, date: rec.date, status: rec.status || "Present",
      in_time: rec.in_time || null, out_time: rec.out_time || null,
      lunch_in: rec.lunch_in || null, lunch_out: rec.lunch_out || null,
      remarks: rec.remarks || ""
    };
    try {
      setSavingIds(s => [...s, empId]);
      if (rec.id) await client.put(`/attendance/${rec.id}`, payload);
      else await client.post("/attendance", payload);

      void logEmployeeActivity({
        targetEmployeeId: rec.employee_id,
        targetEmployeeName: rec.employee_name,
        action: rec.id ? "Attendance Updated" : "Attendance Marked",
        module: "Attendance",
        description: `${rec.employee_name} attendance saved for ${rec.date}.`,
      });

      // optimistic: update attendanceList locally (simplest: re-fetch)
      await fetchAttendance(date);
      setDirtySet(prev => { const n = new Set(prev); n.delete(empId); return n; });
    } catch (err) {
      console.error("save error", err); alert("Save failed for " + rec.employee_name);
    } finally {
      setSavingIds(s => s.filter(x=>x!==empId));
    }
  };

  const saveAll = async () => {
    const toSave = localRecords.filter(r => dirtySet.has(r.employee_id));
    if (toSave.length === 0) return alert("Nothing to save");
    if (!confirm(`Save ${toSave.length} changes?`)) return;
    setGlobalSaving(true);
    for (const rec of toSave) {
      try {
        setSavingIds(s=>[...s, rec.employee_id]);
        const payload = {
          employee_id: rec.employee_id, date: rec.date, status: rec.status || "Present",
          in_time: rec.in_time || null, out_time: rec.out_time || null,
          lunch_in: rec.lunch_in || null, lunch_out: rec.lunch_out || null,
          remarks: rec.remarks || ""
        };
        if (rec.id) await client.put(`/attendance/${rec.id}`, payload);
        else await client.post("/attendance", payload);

        setDirtySet(prev => { const n = new Set(prev); n.delete(rec.employee_id); return n; });
      } catch (err) {
        console.error("saveAll error", err);
      } finally {
        setSavingIds(s => s.filter(x=>x!==rec.employee_id));
      }
    }

    void logEmployeeActivity({
      action: "Attendance Batch Saved",
      module: "Attendance",
      description: `Saved attendance for ${toSave.length} employees on ${date}.`,
    });

    await fetchAttendance(date);
    setGlobalSaving(false);
    alert("All saves completed");
  };

  // mark all helpers
  const markAllPresent = () => {
    const now = new Date(); const hh = String(now.getHours()).padStart(2,"0"); const mm = String(now.getMinutes()).padStart(2,"0");
    const hhmm = `${hh}:${mm}`;
    setLocalRecords(prev => {
      const updated = prev.map(r=> ({...r, status:"Present", in_time: r.in_time || hhmm}));
      setDirtySet(new Set(updated.map(x=>x.employee_id)));
      return updated;
    });
  };
  const markAllAbsent = () => {
    setLocalRecords(prev => {
      const updated = prev.map(r => ({...r, status:"Absent", in_time:"", out_time:"", lunch_in:"", lunch_out:""}));
      setDirtySet(new Set(updated.map(x=>x.employee_id)));
      return updated;
    });
  };

  // Search filter
  const filteredRecords = useMemo(()=> {
    const q = (search||"").trim().toLowerCase();
    if (!q) return localRecords;
    return localRecords.filter(r =>
      String(r.employee_name||"").toLowerCase().includes(q) ||
      String(r.employee_id||"").toLowerCase().includes(q)
    );
  }, [localRecords, search]);

  // Monthly report functions (try range endpoint then fallback to per-day)
  function monthRange(monthStr) {
    const [y,m] = monthStr.split("-").map(Number);
    const first = `${y}-${String(m).padStart(2,"0")}-01`;
    const lastDate = new Date(y, m, 0).getDate();
    const last = `${y}-${String(m).padStart(2,"0")}-${String(lastDate).padStart(2,"0")}`;
    return { from:first, to:last, days:lastDate, year:y, month:m };
  }

  const fetchMonthlyReport = async (monthStr = reportMonth) => {
    const rng = monthRange(monthStr);
    setReportLoading(true);
    setMonthlyReport(null);
    try {
      // try server range
      try {
        const res = await client.get(`/attendance?from=${rng.from}&to=${rng.to}`);

        const rows = res.data || [];
        const agg = aggregateMonthly(rows, rng.days);
        setMonthlyReport(agg);
        setReportLoading(false);
        setShowReportModal(true);
        return;
      } catch (errRange) {
        console.warn("range fetch failed, falling back", errRange);
      }
      // fallback: iterate days
      const all = [];
      for (let d=1; d<=rng.days; d++) {
        const day = `${rng.year}-${String(rng.month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
        try {
          const r = await client.get(`/attendance?date=${day}`);

          all.push(...(r.data||[]));
        } catch {
          console.warn("day failed", day);
        }
      }
      const agg = aggregateMonthly(all, rng.days);
      setMonthlyReport(agg);
      setShowReportModal(true);
    } catch (err) {
      console.error("monthly report failed", err);
      alert("Failed to generate monthly report.");
    } finally {
      setReportLoading(false);
    }
  };

  const aggregateMonthly = (rows, daysInMonth) => {
    const map = {};
    for (const r of rows) {
      const id = r.employee_id;
      if (!map[id]) map[id] = { employee_id:id, employee_name: r.employee_name || `#${id}`, presentDays:0, totalWorked:0, expected:0, daysWithData:0 };
      const w = calcWorkedMinutes(r.in_time, r.out_time, r.lunch_in, r.lunch_out);
      if (w && w.minutes != null) {
        map[id].presentDays += 1;
        map[id].totalWorked += w.minutes;
        map[id].expected += standardMinutes;
        map[id].daysWithData += 1;
      } else {
        map[id].expected += standardMinutes;
      }
    }
    // include employees with zero rows
    for (const emp of employees) {
      if (!map[emp.id]) {
        map[emp.id] = { employee_id: emp.id, employee_name: emp.name || `#${emp.id}`, presentDays:0, totalWorked:0, expected: standardMinutes*daysInMonth, daysWithData:0 };
        map[emp.id].totalDiff = map[emp.id].totalWorked - map[emp.id].expected;
      }
    }
    const arr = Object.values(map).map(m => ({
      ...m,
      avgDaily: m.daysWithData ? Math.round((m.totalWorked / m.daysWithData) * 100) / 100 : 0,
      totalWorkedHHMM: minutesToHHMM(m.totalWorked),
      expectedHHMM: minutesToHHMM(m.expected),
      diffHHMM: (m.totalWorked - m.expected >=0 ? "+" : "-") + minutesToHHMM(Math.abs(m.totalWorked - m.expected)),
      totalWorkedDecimal: minsToDecimalHours(m.totalWorked),
    }));
    // sort by name
    arr.sort((a,b)=> (a.employee_name||"").localeCompare(b.employee_name||""));
    return arr;
  };

  const exportReportCSV = (rows = monthlyReport || []) => {
    if (!rows || rows.length===0) return alert("No report data");
    const header = ["Employee ID","Employee Name","Present Days","Total Worked (HH:MM)","Expected (HH:MM)","Diff (HH:MM)","Total Worked (hrs)"];
    const csv = [header.join(",")].concat(rows.map(r => [
      r.employee_id,
      `"${(r.employee_name||"").replace(/"/g,'""')}"`,
      r.presentDays,
      `"${r.totalWorkedHHMM}"`,
      `"${r.expectedHHMM}"`,
      `"${r.diffHHMM}"`,
      r.totalWorkedDecimal
    ].join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `attendance_report_${reportMonth}.csv`; a.click();
  };

  // small UI helpers
  const isSaving = (empId) => savingIds.includes(empId);

  // render
  return (
    <div className="attendance-page page-container">
      <header className="attendance-header">
        <div className="header-left">
          <h1 className="header-title">
            <FiUser size={24} strokeWidth={2} />
            Attendance Management
          </h1>
          <p className="header-subtitle">Daily attendance with lunch support, quick bulk actions, and reporting.</p>
        </div>
        <div className="header-actions">
          <div className="search-wrap">
            <FiSearch size={18} strokeWidth={2} className="search-icon" />
            <input
              type="text"
              placeholder="Search name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          <IconButton className="outline small" onClick={() => fetchAttendance(date)} title="Refresh">
            <FiRefreshCw /> Refresh
          </IconButton>
          <IconButton className="primary" onClick={saveAll} title="Save All" disabled={globalSaving || dirtySet.size === 0}>
            <FaSave /> {globalSaving ? "Saving..." : `Save All (${dirtySet.size})`}
            {globalSaving && <FaSpinner className="small-spinner" />}
          </IconButton>
        </div>
      </header>

      {/* Controls Card */}
      <div className="attendance-controls-card">
        <div className="controls-left">
          <div className="date-filter">
            <label className="filter-label">Day</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="date-input" />
            <IconButton className="outline small" onClick={() => { setDate(new Date().toISOString().slice(0, 10)); fetchAttendance(new Date().toISOString().slice(0, 10)); }}>
              Today
            </IconButton>
          </div>
        </div>

        <div className="controls-right">
          <div className="settings-group">
            <label className="settings-label">Std Hrs</label>
            <input
              type="number"
              min="0"
              step="0.25"
              value={standardHours}
              onChange={(e) => setStandardHours(Number(e.target.value))}
              className="settings-input"
            />
            <label className="settings-label">Lunch (mins)</label>
            <input
              type="number"
              min="0"
              step="5"
              value={standardLunchMinutes}
              onChange={(e) => setStandardLunchMinutes(Number(e.target.value))}
              className="settings-input"
            />
          </div>
          <IconButton className="outline small" onClick={() => { if (confirm("Mark all Present?")) markAllPresent(); }}>
            Mark All Present
          </IconButton>
          <IconButton className="outline small" onClick={() => { if (confirm("Mark all Absent?")) markAllAbsent(); }}>
            Mark All Absent
          </IconButton>
          <div className="action-buttons">
            <IconButton className="export small" onClick={() => { setShowReportModal(true); fetchMonthlyReport(reportMonth); }}>
              <FiDownload /> Report
            </IconButton>
            <IconButton className="outline small" onClick={() => fetchMonthlyReport(reportMonth)}>
              <FaDownload /> Generate
            </IconButton>
            <button className="btn outline small" onClick={() => navigate("/attendance-history")}>
              Attendance History
            </button>
            <button className="btn outline small" onClick={() => navigate("/salary-history")}>
              Salary History
            </button>
            <button className="btn outline small" onClick={() => navigate("/login-activity")}>
              Login Activity
            </button>
          </div>
        </div>
      </div>
     
  {/* LOGIN ACTIVITY BUTTON */}

      {/* Table Card */}
      {loading ? (
        <div className="loading-msg">Loading…</div>
      ) : (
        <AttendanceTable
          records={filteredRecords}
          handleChange={handleChange}
          handleStatusChange={handleStatusChange}
          saveAttendance={saveAttendance}
          isSaving={isSaving}
          dirtySet={dirtySet}
          calcWorkedMinutes={calcWorkedMinutes}
          minutesToHHMM={minutesToHHMM}
          minsToDecimalHours={minsToDecimalHours}
          standardMinutes={standardMinutes}
          onOpenDetails={setShowEmployeeModal}
        />
      )}




      {/* Employee detail modal (simple slide-down panel) */}
      {showEmployeeModal && (() => {
        const emp = localRecords.find(r => r.employee_id === showEmployeeModal);
        if (!emp) return null;
        const _monthlyDays = 30;
        // quick monthly preview (sample)
        return (
          <div style={{
            position:"fixed", right:20, top:80, width:420, maxHeight:"70vh", overflow:"auto",
            background:"white", borderRadius:12, boxShadow:"0 18px 60px rgba(11,18,32,0.2)", zIndex:1200, border:"1px solid rgba(11,18,32,0.06)"
          }}>
            <div style={{padding:12, borderBottom:"1px solid rgba(11,18,32,0.04)", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <div>
                <div style={{fontWeight:800}}>{emp.employee_name}</div>
                <div className="emp-sub">ID: {emp.employee_id}</div>
              </div>
              <div style={{display:"flex", gap:8}}>
                <button className="btn outline small" onClick={()=>setShowEmployeeModal(null)}>Close</button>
              </div>
            </div>

            <div style={{padding:12}}>
              <div style={{marginBottom:10, fontWeight:700}}>Quick Stats</div>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
                <div style={{background:"#fbfbff", padding:8, borderRadius:8}}>
                  <div className="kv">Today Worked</div>
                  <div style={{fontWeight:800}}>{(() => {
                    const w = calcWorkedMinutes(emp.in_time, emp.out_time, emp.lunch_in, emp.lunch_out);
                    return w ? minutesToHHMM(w.minutes) : "—";
                  })()}</div>
                </div>
                <div style={{background:"#fff8f3", padding:8, borderRadius:8}}>
                  <div className="kv">Status</div>
                  <div style={{fontWeight:800}}><Badge status={emp.status || "—"} /></div>
                </div>
                <div style={{background:"#f3fff7", padding:8, borderRadius:8}}>
                  <div className="kv">In</div>
                  <div style={{fontWeight:800}}>{emp.in_time || "—"}</div>
                </div>
                <div style={{background:"#fff7fb", padding:8, borderRadius:8}}>
                  <div className="kv">Out</div>
                  <div style={{fontWeight:800}}>{emp.out_time || "—"}</div>
                </div>
              </div>

              <div style={{marginTop:12}}>
                <div className="kv">Remarks</div>
                <div style={{marginTop:6, minHeight:40, padding:8, background:"#fbfbff", borderRadius:8}}>{emp.remarks || "—"}</div>
              </div>

              <div style={{marginTop:12, display:"flex", gap:8, justifyContent:"flex-end"}}>
                <button className="btn outline small" onClick={()=>setShowEmployeeModal(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Report Modal */}
      {showReportModal && (
        <div style={{
          position:"fixed", left:"50%", top:"50%", transform:"translate(-50%,-50%)", width:"85%", maxWidth:1100, maxHeight:"85vh", overflow:"auto",
          background:"white", borderRadius:12, boxShadow:"0 30px 80px rgba(11,18,32,0.25)", zIndex:1300, border:"1px solid rgba(11,18,32,0.06)"
        }}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:14, borderBottom:"1px solid rgba(11,18,32,0.04)"}}>
            <div style={{display:"flex", gap:12, alignItems:"center"}}>
              <FaCalendarAlt /> <div style={{fontWeight:800}}>Monthly Attendance Report — {reportMonth}</div>
            </div>
            <div style={{display:"flex", gap:8}}>
              <button className="btn outline small" onClick={()=>setShowReportModal(false)}>Close</button>
              <button className="btn primary small" onClick={()=>exportReportCSV(monthlyReport)} disabled={!monthlyReport}><FaDownload /> Export CSV</button>
            </div>
          </div>

          <div style={{padding:14}}>
            <div style={{display:"flex", gap:12, alignItems:"center", marginBottom:10}}>
              <input type="month" value={reportMonth} onChange={(e)=>setReportMonth(e.target.value)} style={{padding:8,borderRadius:8,border:"1px solid #e6e9f2"}} />
              <button className="btn outline small" onClick={()=>fetchMonthlyReport(reportMonth)} disabled={reportLoading}>{reportLoading ? "Generating..." : "Regenerate"}</button>
              <div className="download-note">{monthlyReport ? `${monthlyReport.length} employees` : ""}</div>
            </div>

            {reportLoading ? <div className="loading">Generating report…</div> : (monthlyReport && monthlyReport.length>0 ? (
              <div className="report-table" style={{maxHeight:"60vh", overflow:"auto"}}>
                <table style={{width:"100%", borderCollapse:"collapse"}}>
                  <thead>
                    <tr>
                      <th style={{padding:10, textAlign:"left"}}>Employee</th>
                      <th style={{padding:10}}>Present</th>
                      <th style={{padding:10}}>Worked</th>
                      <th style={{padding:10}}>Expected</th>
                      <th style={{padding:10}}>Diff</th>
                      <th style={{padding:10}}>Avg (hrs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyReport.map(row => (
                      <tr key={row.employee_id}>
                        <td style={{padding:10}}>{row.employee_name} <div className="emp-sub">#{row.employee_id}</div></td>
                        <td style={{padding:10}}>{row.presentDays}</td>
                        <td style={{padding:10}}>{row.totalWorkedHHMM}</td>
                        <td style={{padding:10}}>{row.expectedHHMM}</td>
                        <td style={{padding:10}}>{row.diffHHMM}</td>
                        <td style={{padding:10}}>{row.totalWorkedDecimal ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="hint">No report data yet. Generate or adjust month.</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
